import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AlertCircle, Plus, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../i18n/I18nContext';
import {
  getStudentAssessment,
  getStudentRiskHistory,
  suppressRecommendation,
  createMentorRecommendation,
  getStudentEnrolledContent,
} from '../../api/mentorAi';
import { getSkillTaxonomy } from '../../api/skills';
import { errorText } from '../../utils/errorText';
import { failure } from '../teacher/apiError';
import { formatDateTime, formatRelative } from '../../utils/datetime';
import usePageTitle from '../../hooks/usePageTitle';
import Dropdown from '../../components/Dropdown/Dropdown';
import Modal from '../../components/Modal/Modal';
import PageHead from '../../components/PageHead/PageHead';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import SubTabs from '../../components/SubTabs/SubTabs';
import Avatar from '../../components/Avatar/Avatar';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import LoadingState from '../../components/LoadingState/LoadingState';
import RiskScoreTrendChart from '../../components/RiskScoreTrendChart/RiskScoreTrendChart';
import {
  getRiskBand,
  getRiskBandClass,
  getRiskBandLabel,
  getTriggerLabel,
} from '../../constants/riskBand';
import { riskFactorText } from '../../components/Student360/riskFactor';
import './MentorStudentDetail.css';

/**
 * Story 19-2-b: Assessment & Recommendations tab on student detail page.
 * Story 20-1-b: Suppress action UI (wired into recommendation cards).
 * Story 20-2-b: Manual recommendation authoring modal.
 *
 * Route: /mentor/students/:studentId
 * Teacher route: /teacher/students/:studentId/assessment (same component)
 *
 * IMPORTANT — Decision #17: narrativeForMentor is MENTOR-ONLY.
 * This component is gated via MentorProtectedRoute (MENTOR | ADMIN roles).
 * Do NOT reuse this component or its sub-components on any student-facing
 * surface without removing the narrativeForMentor display block.
 *
 * Decision #8: TEACHER and MENTOR roles have the same access here.
 * The MentorProtectedRoute already enforces MENTOR | ADMIN; TEACHER users
 * reach this page via TeacherProtectedRoute at the parallel route above.
 * The backend lets a TEACHER or MENTOR in only for students assigned to them;
 * an ADMIN of the tenant can open any student.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ACTIVE_STATES = ['PENDING', 'SEEN', 'ACCEPTED'];
const INACTIVE_STATES = ['DISMISSED', 'COMPLETED', 'MENTOR_SUPPRESSED', 'SUPERSEDED', 'EXPIRED'];

const STATE_LABEL_KEYS = {
  PENDING: 'directory.studentDetail.states.pending',
  SEEN: 'directory.studentDetail.states.seen',
  ACCEPTED: 'directory.studentDetail.states.accepted',
  DISMISSED: 'directory.studentDetail.states.dismissed',
  COMPLETED: 'directory.studentDetail.states.completed',
  MENTOR_SUPPRESSED: 'directory.studentDetail.states.mentorSuppressed',
  SUPERSEDED: 'directory.studentDetail.states.superseded',
  EXPIRED: 'directory.studentDetail.states.expired',
};

const SUPPRESS_PRESETS = [
  { value: 'Prerequisite not met', labelKey: 'directory.studentDetail.suppressModal.presets.prerequisiteNotMet' },
  { value: 'Not relevant right now', labelKey: 'directory.studentDetail.suppressModal.presets.notRelevantNow' },
  { value: 'Student not ready for this', labelKey: 'directory.studentDetail.suppressModal.presets.studentNotReady' },
  { value: 'Other', labelKey: 'directory.studentDetail.suppressModal.presets.other' },
];

const TARGET_TYPES = ['MODULE', 'LESSON', 'QUIZ'];

const TARGET_TYPE_LABEL_KEYS = {
  MODULE: 'directory.studentDetail.targetTypes.module',
  LESSON: 'directory.studentDetail.targetTypes.lesson',
  QUIZ: 'directory.studentDetail.targetTypes.quiz',
};

const SOURCE_BADGE = {
  AI: null,              // No badge for AI source per Decision #19
  AI_BOOTSTRAP: 'directory.studentDetail.recommendations.sourceBadgeStarterPlan',
  MENTOR: null,          // Resolved dynamically using createdByUser.name
};

// A risk band's colour name -> the tone it is drawn in
const RISK_TONE = { red: 'danger', amber: 'warning', green: 'success', grey: 'neutral' };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Suppressing a recommendation only stamps mentorSuppressedAt on the backend; its state stays
 * PENDING, SEEN or ACCEPTED. Show such a card as MENTOR_SUPPRESSED, or it is back in the active
 * list after every reload. A card that was since dismissed, completed or expired keeps the
 * state that says more.
 */
function withEffectiveState(rec) {
  return rec.mentorSuppressedAt && ACTIVE_STATES.includes(rec.state)
    ? { ...rec, state: 'MENTOR_SUPPRESSED' }
    : rec;
}

/**
 * What a recommendation points at, by name. The backend names every target it can still find;
 * one it cannot (the content was removed) is said to be gone rather than shown as a bare id.
 */
function targetTitle(rec, t) {
  return rec.targetName || t('directory.studentDetail.recommendations.targetUnavailable');
}

/**
 * Translate a risk band value via risk.band.<value>, falling back to the
 * framework-agnostic getRiskBandLabel() for unmapped/no-data values.
 */
function riskBandLabel(t, band) {
  const key = band ? `risk.band.${band}` : 'risk.band.noData';
  const val = t(key);
  return val !== key ? val : getRiskBandLabel(band);
}

/**
 * Translate a raw trigger value via risk.trigger.<value>, falling back to
 * the framework-agnostic getTriggerLabel() title-casing for unmapped values.
 */
function riskTriggerLabel(t, trigger) {
  const key = trigger ? `risk.trigger.${trigger}` : 'risk.trigger.unknown';
  const val = t(key);
  return val !== key ? val : getTriggerLabel(trigger);
}

/**
 * A risk factor is a free-form code from the assessment ("low_quiz_scores", "LOW_ENGAGEMENT").
 * The common ones are translated via risk.factor.<code>, then the Student 360 dictionary; a code
 * neither knows reads "Other factor" in the UI language, with the raw code as the tooltip.
 * Returns { label, hint }.
 */
function riskFactorLabel(t, factor) {
  const key = `risk.factor.${String(factor || '').toLowerCase()}`;
  const val = t(key);
  return val !== key ? { label: val, hint: '' } : riskFactorText(t, factor);
}

// ---------------------------------------------------------------------------
// Risk score badge
// ---------------------------------------------------------------------------

function RiskBadge({ score, size = 'md' }) {
  const { t } = useI18n();
  const band = getRiskBand(score);
  const colorClass = getRiskBandClass(band);
  const scoreText = score != null ? `${score}/10` : t('directory.studentDetail.riskBadge.noData');
  return (
    <span
      className={`msd-risk-badge msd-risk-badge--${colorClass} msd-risk-badge--${size}`}
      data-tone={RISK_TONE[colorClass] || 'neutral'}
      title={t('directory.studentDetail.riskBadge.title', { score: scoreText, band: riskBandLabel(t, band) })}
    >
      {scoreText}
      {band && <span className="msd-risk-badge-label"> {riskBandLabel(t, band)}</span>}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Suppress modal (Story 20-1-b)
// ---------------------------------------------------------------------------

function SuppressModal({ recommendation, onConfirm, onClose, submitting }) {
  const { t } = useI18n();
  const [selected, setSelected] = useState(SUPPRESS_PRESETS[0].value);
  const [otherText, setOtherText] = useState('');

  const reason = selected === 'Other' ? otherText.trim() : selected;
  const isOtherEmpty = selected === 'Other' && !otherText.trim();
  const canSubmit = !isOtherEmpty && !submitting;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onConfirm(recommendation.id, reason);
  };

  return (
    <Modal
      open
      onClose={submitting ? undefined : onClose}
      title={t('directory.studentDetail.suppressModal.title')}
      size="md"
      className="msd-modal"
      footer={(
        <>
          <button type="button" className="ds-btn ds-btn-secondary" onClick={onClose} disabled={submitting}>
            {t('directory.studentDetail.suppressModal.cancel')}
          </button>
          <button
            type="button"
            className={`ds-btn ds-btn-danger-fill${submitting ? ' is-loading' : ''}`}
            onClick={handleSubmit}
            disabled={!canSubmit}
            aria-busy={submitting || undefined}
          >
            {t('directory.studentDetail.suppressModal.suppress')}
          </button>
        </>
      )}
    >
      <p className="msd-modal-body">
        {t('directory.studentDetail.suppressModal.body')}
      </p>

      <div className="msd-suppress-presets">
        {SUPPRESS_PRESETS.map((preset) => (
          <label key={preset.value} className="msd-suppress-preset ui-check">
            <input
              type="radio"
              name="suppress-reason"
              value={preset.value}
              checked={selected === preset.value}
              onChange={() => setSelected(preset.value)}
            />
            <span>{t(preset.labelKey)}</span>
          </label>
        ))}
      </div>

      {selected === 'Other' && (
        <div className="msd-suppress-other">
          <textarea
            className="msd-suppress-textarea"
            placeholder={t('directory.studentDetail.suppressModal.otherPlaceholder')}
            value={otherText}
            onChange={(e) => setOtherText(e.target.value.slice(0, 500))}
            rows={3}
            aria-label={t('directory.studentDetail.suppressModal.otherAria')}
          />
          <p className="ui-hint msd-char-counter">{otherText.length}/500</p>
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Add recommendation modal (Story 20-2-b)
// ---------------------------------------------------------------------------

function AddRecommendationModal({ studentId, studentName, onSuccess, onClose }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [targetType, setTargetType] = useState('MODULE');
  const [targetId, setTargetId] = useState('');
  const [reasoning, setReasoning] = useState('');
  const [priority, setPriority] = useState(1);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [skillQuery, setSkillQuery] = useState('');

  const [enrolledContent, setEnrolledContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(true);
  const [contentError, setContentError] = useState(null);

  const [skills, setSkills] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Load enrolled content for target picker
  useEffect(() => {
    let active = true;
    setContentLoading(true);
    setContentError(null);
    getStudentEnrolledContent(studentId)
      .then((data) => {
        if (!active) return;
        const courses = Array.isArray(data?.courses) ? data.courses
          : Array.isArray(data) ? data
          : [];
        setEnrolledContent(courses);
      })
      .catch(() => {
        if (!active) return;
        // Keep the picker rendering (empty) and say why it is empty
        setEnrolledContent([]);
        setContentError(t('directory.studentDetail.addRecModal.contentErrorNote'));
      })
      .finally(() => { if (active) setContentLoading(false); });
    return () => { active = false; };
  }, [studentId, t]);

  // Load skill taxonomy (reuse existing hook from 16-1-b)
  useEffect(() => {
    let active = true;
    setSkillsLoading(true);
    getSkillTaxonomy()
      .then((data) => {
        if (!active) return;
        const list = Array.isArray(data) ? data
          : Array.isArray(data?.data) ? data.data
          : [];
        setSkills(list);
      })
      .catch(() => { if (active) setSkills([]); })
      .finally(() => { if (active) setSkillsLoading(false); });
    return () => { active = false; };
  }, []);

  // Reset targetId when type changes
  const handleTypeChange = (type) => {
    setTargetType(type);
    setTargetId('');
  };

  // Build flat target options from enrolled content tree, filtered by type
  const targetOptions = React.useMemo(() => {
    if (!enrolledContent) return [];
    const opts = [];
    (enrolledContent || []).forEach((course) => {
      if (targetType === 'MODULE') {
        (course.modules || []).forEach((mod) => {
          opts.push({ id: mod.id, label: `${course.title} > ${mod.title}` });
        });
      } else if (targetType === 'LESSON') {
        (course.modules || []).forEach((mod) => {
          (mod.lessons || []).forEach((lesson) => {
            opts.push({ id: lesson.id, label: `${course.title} > ${mod.title} > ${lesson.title}` });
          });
        });
        // Lessons that sit outside any module hang on the course itself
        (course.lessons || []).forEach((lesson) => {
          opts.push({ id: lesson.id, label: `${course.title} > ${lesson.title}` });
        });
      } else if (targetType === 'QUIZ') {
        (course.modules || []).forEach((mod) => {
          (mod.quizzes || []).forEach((quiz) => {
            opts.push({ id: quiz.id, label: `${course.title} > ${mod.title} > ${quiz.title}` });
          });
        });
        (course.quizzes || []).forEach((quiz) => {
          opts.push({ id: quiz.id, label: `${course.title} > ${quiz.title}` });
        });
      }
    });
    return opts;
  }, [enrolledContent, targetType]);

  // Skill autocomplete
  const filteredSkills = skills.filter(
    (s) =>
      !selectedSkills.find((sel) => sel.slug === s.slug) &&
      (s.name.toLowerCase().includes(skillQuery.toLowerCase()) ||
        s.slug.toLowerCase().includes(skillQuery.toLowerCase()))
  );

  const toggleSkill = (skill) => {
    setSelectedSkills((prev) =>
      prev.find((s) => s.slug === skill.slug)
        ? prev.filter((s) => s.slug !== skill.slug)
        : [...prev, skill]
    );
  };

  const removeSkill = (slug) => {
    setSelectedSkills((prev) => prev.filter((s) => s.slug !== slug));
  };

  // Validation
  const isValid =
    targetType &&
    targetId &&
    reasoning.trim().length > 0 &&
    reasoning.trim().length <= 500 &&
    selectedSkills.length > 0;

  const handleSubmit = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await createMentorRecommendation(studentId, {
        targetType,
        targetId,
        reasoning: reasoning.trim(),
        skillSlugs: selectedSkills.map((s) => s.slug),
        priority,
      });
      // Build optimistic card to pass back
      const optimisticCard = {
        ...result,
        sourceType: 'MENTOR',
        state: 'PENDING',
        createdByUser: { id: user?.id, name: user?.name || user?.email, avatarUrl: null },
        skills: selectedSkills,
        reasoning: reasoning.trim(),
        targetType,
        targetId,
        targetName: targetOptions.find((o) => o.id === targetId)?.label || null,
        createdAt: new Date().toISOString(),
      };
      onSuccess(optimisticCard);
    } catch (err) {
      const msg = err?.message || '';
      if (msg.toLowerCase().includes('target')) {
        setSubmitError(t('directory.studentDetail.errors.targetNotAvailable'));
      } else if (msg.toLowerCase().includes('skill')) {
        setSubmitError(t('directory.studentDetail.errors.skillsUnavailable'));
      } else if (err?.code === 0 || msg.toLowerCase().includes('network')) {
        setSubmitError(t('directory.studentDetail.errors.networkError'));
      } else {
        setSubmitError(errorText(err, t('directory.studentDetail.errors.addRecGeneric')));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const itemPlaceholder = targetOptions.length === 0
    ? t(`directory.studentDetail.addRecModal.noItemsAvailable.${targetType.toLowerCase()}`)
    : t(`directory.studentDetail.addRecModal.selectItem.${targetType.toLowerCase()}`);

  return (
    <Modal
      open
      onClose={submitting ? undefined : onClose}
      title={t('directory.studentDetail.addRecModal.titleFor', { name: studentName })}
      size="lg"
      className="msd-modal"
      footer={(
        <>
          <button type="button" className="ds-btn ds-btn-secondary" onClick={onClose} disabled={submitting}>
            {t('directory.studentDetail.addRecModal.cancel')}
          </button>
          <button
            type="button"
            className={`ds-btn ds-btn-primary${submitting ? ' is-loading' : ''}`}
            onClick={handleSubmit}
            disabled={!isValid || submitting}
            aria-busy={submitting || undefined}
          >
            {t('directory.studentDetail.addRecModal.add')}
          </button>
        </>
      )}
    >
      {submitError && (
        <div className="ui-alert msd-modal-error" role="alert">
          <AlertCircle aria-hidden="true" />
          <p>{submitError}</p>
        </div>
      )}

      <div className="msd-form">
        {/* Target type */}
        <div className="ui-field">
          <span className="ui-label">
            {t('directory.studentDetail.addRecModal.targetTypeFieldLabel')} <span className="ui-req" aria-hidden="true">*</span>
          </span>
          <div className="ui-chip-row msd-type-row">
            {TARGET_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                className={`ui-chip msd-type-btn${targetType === type ? ' is-active' : ''}`}
                aria-pressed={targetType === type}
                onClick={() => handleTypeChange(type)}
              >
                {t(TARGET_TYPE_LABEL_KEYS[type])}
              </button>
            ))}
          </div>
        </div>

        {/* Target select */}
        <div className="ui-field">
          <label className="ui-label" htmlFor="add-rec-target">
            {t('directory.studentDetail.addRecModal.targetFieldLabel')} <span className="ui-req" aria-hidden="true">*</span>
          </label>
          {contentLoading ? (
            <LoadingState compact message={t('directory.studentDetail.addRecModal.loadingContent')} />
          ) : (
            <>
              {contentError && (
                <p className="ui-hint msd-field-note">{contentError}</p>
              )}
              <Dropdown
                id="add-rec-target"
                value={targetId}
                onChange={(val) => setTargetId(val)}
                options={[
                  { value: '', label: itemPlaceholder },
                  ...targetOptions.map((opt) => ({
                    value: opt.id,
                    label: opt.label,
                  })),
                ]}
                placeholder={itemPlaceholder}
                ariaLabel={t('directory.studentDetail.addRecModal.targetFieldLabel')}
              />
            </>
          )}
        </div>

        {/* Reasoning */}
        <div className="ui-field">
          <label className="ui-label" htmlFor="add-rec-reasoning">
            {t('directory.studentDetail.addRecModal.reasoningLabel')} <span className="ui-req" aria-hidden="true">*</span>
          </label>
          <textarea
            id="add-rec-reasoning"
            className="msd-form-textarea"
            placeholder={t('directory.studentDetail.addRecModal.reasoningPlaceholder')}
            value={reasoning}
            onChange={(e) => setReasoning(e.target.value.slice(0, 500))}
            rows={3}
          />
          <p className={`ui-hint msd-char-counter${reasoning.length >= 480 ? ' msd-char-counter--warn' : ''}`}>
            {reasoning.length}/500
          </p>
        </div>

        {/* Priority */}
        <div className="ui-field msd-priority">
          <label className="ui-label" htmlFor="add-rec-priority">{t('directory.studentDetail.addRecModal.priorityLabel')}</label>
          <Dropdown
            id="add-rec-priority"
            value={priority}
            onChange={(val) => setPriority(Number(val))}
            options={[1, 2, 3, 4, 5].map((p) => ({
              value: p,
              label: String(p),
            }))}
            ariaLabel={t('directory.studentDetail.addRecModal.priorityLabel')}
          />
        </div>

        {/* Skills multi-select */}
        <div className="ui-field">
          <span className="ui-label">
            {t('directory.studentDetail.addRecModal.skillsLabel')} <span className="ui-req" aria-hidden="true">*</span>
          </span>
          {/* Selected skill chips */}
          {selectedSkills.length > 0 && (
            <div className="msd-skill-chips">
              {selectedSkills.map((s) => (
                <span key={s.slug} className="msd-skill-chip ds-badge ds-badge-info">
                  {s.name}
                  <button
                    type="button"
                    className="msd-skill-chip-remove"
                    onClick={() => removeSkill(s.slug)}
                    aria-label={t('directory.studentDetail.addRecModal.removeSkillAria', { name: s.name })}
                  >
                    <X aria-hidden="true" />
                  </button>
                </span>
              ))}
            </div>
          )}
          {/* Search input */}
          <input
            type="text"
            className="msd-form-input"
            placeholder={skillsLoading ? t('directory.studentDetail.addRecModal.skillsSearchLoading') : t('directory.studentDetail.addRecModal.skillsSearchPlaceholder')}
            value={skillQuery}
            onChange={(e) => setSkillQuery(e.target.value)}
            disabled={skillsLoading}
            aria-label={t('directory.studentDetail.addRecModal.skillsSearchAria')}
          />
          {skillQuery && filteredSkills.length > 0 && (
            <ul className="msd-skill-dropdown">
              {filteredSkills.slice(0, 10).map((s) => (
                <li key={s.slug}>
                  <button
                    type="button"
                    className="msd-skill-option"
                    onClick={() => { toggleSkill(s); setSkillQuery(''); }}
                  >
                    {s.name}
                    <span className="msd-skill-slug">{s.slug}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {skillQuery && filteredSkills.length === 0 && (
            <p className="ui-hint msd-skill-empty">{t('directory.studentDetail.addRecModal.noMatchingSkills')}</p>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Recommendation card (19-2-b + 20-1-b suppress action)
// ---------------------------------------------------------------------------

function RecommendationCard({ rec, onSuppress }) {
  const { t } = useI18n();

  const isActive = ACTIVE_STATES.includes(rec.state);
  const canSuppress = isActive;

  const sourceBadgeKey = rec.sourceType === 'AI_BOOTSTRAP'
    ? SOURCE_BADGE.AI_BOOTSTRAP
    : null; // a MENTOR card shows its attribution row below instead

  const targetTypeLabel = rec.targetType
    ? t(TARGET_TYPE_LABEL_KEYS[rec.targetType]) || rec.targetType
    : t('directory.studentDetail.recommendations.targetTypeFallback');
  const stateLabel = STATE_LABEL_KEYS[rec.state] ? t(STATE_LABEL_KEYS[rec.state]) : rec.state;

  return (
    <div className="msd-rec-card">
      {/* Card header row */}
      <div className="msd-rec-card-header">
        <div className="msd-rec-type-row">
          <span className="msd-rec-target-type ds-badge ds-badge-purple">{targetTypeLabel}</span>
          <span className="msd-rec-state ds-badge ds-badge-neutral">{stateLabel}</span>
          {sourceBadgeKey && (
            <span className="msd-rec-source-badge ds-badge ds-badge-success">{t(sourceBadgeKey)}</span>
          )}
        </div>

        {/* "..." menu — only on active cards per Decision #6 */}
        {canSuppress && (
          <ActionMenu
            className="msd-kebab"
            ariaLabel={t('directory.studentDetail.recommendations.kebabAria')}
            items={[{
              key: 'suppress',
              label: t('directory.studentDetail.recommendations.suppressAction'),
              tone: 'danger',
              onSelect: () => onSuppress(rec),
            }]}
          />
        )}
      </div>

      {/* Target name */}
      <div className="msd-rec-target-name">
        {targetTitle(rec, t)}
      </div>

      {/* Reasoning */}
      {rec.reasoning && (
        <p className="msd-rec-reasoning">{rec.reasoning}</p>
      )}

      {/* Skill chips */}
      {rec.skills && rec.skills.length > 0 && (
        <div className="msd-rec-skill-row">
          {rec.skills.map((s) => (
            <span key={s.slug || s.id} className="msd-rec-skill-chip ds-badge ds-badge-info">
              {s.name || s.slug}
            </span>
          ))}
        </div>
      )}

      {/* Mentor attribution — Decision #19: "From [Mentor Name]" badge with avatar */}
      {rec.sourceType === 'MENTOR' && rec.createdByUser && (
        <div className="msd-rec-mentor-row">
          <Avatar name={rec.createdByUser.name} id={rec.createdByUser.id} size="sm" />
          <span className="msd-mentor-label">
            {t('directory.studentDetail.recommendations.mentorFrom', { name: rec.createdByUser.name })}
          </span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function MentorStudentDetail() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { studentId } = useParams();
  const navigate = useNavigate();
  // A TEACHER opens this page from their own workspace, so "back" stays in it
  const isTeacher = user?.role === 'TEACHER';

  const [activeTab, setActiveTab] = useState('assessment');
  const [studentName, setStudentName] = useState(t('directory.studentDetail.defaultStudentName'));

  // Assessment tab state
  const [assessmentData, setAssessmentData] = useState(null);
  const [assessmentLoading, setAssessmentLoading] = useState(true);
  const [assessmentError, setAssessmentError] = useState(null);

  // The browser tab and the last top-bar crumb carry the student's name once the assessment has
  // come back with it; until then the route's generic title ("O'quvchi tafsiloti") stands in.
  usePageTitle(assessmentData?.student?.name || t('topbar.title.studentDetail'));

  // Risk history state (trend chart)
  const [riskHistory, setRiskHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState(null);

  // Recommendation lists (managed locally for optimistic updates)
  const [activeRecs, setActiveRecs] = useState([]);
  const [inactiveRecs, setInactiveRecs] = useState([]);
  const [suppressedExpanded, setSuppressedExpanded] = useState(false);

  // Suppress modal
  const [suppressTarget, setSuppressTarget] = useState(null);
  const [suppressSubmitting, setSuppressSubmitting] = useState(false);

  // Add recommendation modal
  const [addRecOpen, setAddRecOpen] = useState(false);

  // ---------------------------------------------------------------------------
  // Fetch data
  // ---------------------------------------------------------------------------

  const loadAssessment = useCallback(async () => {
    setAssessmentLoading(true);
    setAssessmentError(null);
    try {
      const data = await getStudentAssessment(studentId);
      setAssessmentData(data);
      if (data?.student?.name) setStudentName(data.student.name);

      // Split recommendations into active / inactive
      const recs = (Array.isArray(data?.recommendations) ? data.recommendations : [])
        .map(withEffectiveState);
      // MENTOR-source cards pinned at top per Decision #19
      const active = recs
        .filter((r) => ACTIVE_STATES.includes(r.state))
        .sort((a, b) => {
          if (a.sourceType === 'MENTOR' && b.sourceType !== 'MENTOR') return -1;
          if (b.sourceType === 'MENTOR' && a.sourceType !== 'MENTOR') return 1;
          return 0;
        });
      const inactive = recs.filter((r) => INACTIVE_STATES.includes(r.state));
      setActiveRecs(active);
      setInactiveRecs(inactive);
    } catch (err) {
      setAssessmentError(err?.code === 403
        ? { forbidden: true, detail: t('courses.noAccess') }
        : failure(err, t('directory.studentDetail.errors.assessmentLoad')));
    } finally {
      setAssessmentLoading(false);
    }
  }, [studentId, t]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const data = await getStudentRiskHistory(studentId, { limit: 12 });
      const history = Array.isArray(data?.history) ? data.history
        : Array.isArray(data) ? data
        : [];
      setRiskHistory(history);
    } catch (err) {
      // A missing history endpoint/record is non-fatal — the chart already
      // renders its own "not enough data" empty state for an empty array.
      if (err?.code === 404) {
        setRiskHistory([]);
      } else {
        setHistoryError(errorText(err, t('directory.studentDetail.errors.historyLoad')));
      }
    } finally {
      setHistoryLoading(false);
    }
  }, [studentId, t]);

  useEffect(() => {
    loadAssessment();
    loadHistory();
  }, [loadAssessment, loadHistory]);

  // ---------------------------------------------------------------------------
  // Suppress flow (Story 20-1-b)
  // ---------------------------------------------------------------------------

  const handleSuppressClick = (rec) => {
    setSuppressTarget(rec);
  };

  const handleSuppressConfirm = async (recId, reason) => {
    // Optimistic: move card from active to suppressed immediately
    const rec = activeRecs.find((r) => r.id === recId);
    if (!rec) return;

    const suppressed = {
      ...rec,
      state: 'MENTOR_SUPPRESSED',
      mentorSuppressedAt: new Date().toISOString(),
      mentorSuppressedReason: reason,
    };
    setActiveRecs((prev) => prev.filter((r) => r.id !== recId));
    setInactiveRecs((prev) => [suppressed, ...prev]);
    setSuppressTarget(null);

    setSuppressSubmitting(true);
    try {
      await suppressRecommendation(recId, reason);
      toast.success(t('directory.studentDetail.toasts.suppressed'));
    } catch (err) {
      // Rollback optimistic update
      setActiveRecs((prev) => [rec, ...prev]);
      setInactiveRecs((prev) => prev.filter((r) => r.id !== recId));
      toast.error(t('directory.studentDetail.toasts.suppressError'));
    } finally {
      setSuppressSubmitting(false);
    }
  };

  const handleSuppressClose = () => {
    setSuppressTarget(null);
  };

  // ---------------------------------------------------------------------------
  // Add recommendation flow (Story 20-2-b)
  // ---------------------------------------------------------------------------

  const handleAddRecSuccess = (newRec) => {
    // Prepend with mentor avatar badge at top per Decision #19
    setActiveRecs((prev) => [newRec, ...prev]);
    setAddRecOpen(false);
    toast.success(t('directory.studentDetail.toasts.recommendationAdded'));
  };

  // ---------------------------------------------------------------------------
  // Render assessment tab content
  // ---------------------------------------------------------------------------

  function renderAssessmentTab() {
    if (assessmentLoading) {
      // Bones in the shape of the page: the assessment card (a chart and a few lines) beside the recommendations
      return (
        <div className="msd-assessment-content" role="status" aria-busy="true" aria-label={t('ui.loading')}>
          <section className="section-card msd-section" aria-hidden="true">
            <div className="section-head"><div className="ui-skel msd-skel-title" /></div>
            <div className="ui-skel msd-skel-chart" />
            <div className="ui-skel msd-skel-line" />
            <div className="ui-skel msd-skel-line msd-skel-line--short" />
          </section>
          <section className="section-card msd-section" aria-hidden="true">
            <div className="section-head"><div className="ui-skel msd-skel-title" /></div>
            <div className="ui-skel msd-skel-card" />
            <div className="ui-skel msd-skel-card" />
          </section>
          <span className="visually-hidden">{t('ui.loading')}</span>
        </div>
      );
    }

    if (assessmentError) {
      return assessmentError.forbidden ? (
        <PageEmptyState variant="forbidden" size="sm" description={assessmentError.detail} />
      ) : (
        <PageEmptyState
          variant="error"
          size="sm"
          title={assessmentError.title}
          description={assessmentError.detail}
          onRetry={loadAssessment}
        />
      );
    }

    const assessment = assessmentData?.assessment;

    return (
      <div className="msd-assessment-content">
        {/* Assessment section */}
        <section className="section-card msd-section msd-section--assessment">
          <div className="section-head">
            <h2 className="section-head__title">{t('directory.studentDetail.assessment.sectionTitle')}</h2>
            {assessment && <RiskBadge score={assessment.riskScore} size="lg" />}
          </div>

          {!assessment ? (
            <InlineEmpty kind="empty" title={t('directory.studentDetail.assessment.empty')} />
          ) : (
            <>
              {/* Trend chart */}
              <div className="msd-trend-chart-wrap">
                <RiskScoreTrendChart
                  history={riskHistory}
                  loading={historyLoading}
                  error={historyError}
                />
              </div>

              {/* Assessment metadata */}
              <div className="msd-assessment-meta">
                <span
                  className="msd-meta-item"
                  title={assessment.createdAt ? formatDateTime(assessment.createdAt, { lang, year: 'always' }) : ''}
                >
                  {t('directory.studentDetail.assessment.assessedLabel', {
                    time: assessment.createdAt ? formatRelative(assessment.createdAt, { lang }) : '-',
                  })}
                </span>
                <span className="msd-meta-sep" aria-hidden="true">·</span>
                <span className="msd-meta-item">
                  {t('directory.studentDetail.assessment.triggerLabel', { trigger: riskTriggerLabel(t, assessment.trigger) })}
                </span>
                {assessment.locale && (
                  <span className="msd-locale-badge ds-badge ds-badge-info">{assessment.locale}</span>
                )}
              </div>

              {/* Risk factors */}
              {assessment.riskFactors && assessment.riskFactors.length > 0 && (
                <div className="msd-risk-factors">
                  <span className="msd-risk-factors-label">{t('directory.studentDetail.assessment.riskFactorsLabel')}</span>
                  <ul className="msd-risk-factors-list">
                    {assessment.riskFactors.map((f, i) => {
                      const { label, hint } = riskFactorLabel(t, f);
                      return <li key={i} className="ds-badge ds-badge-brand" title={hint || undefined}>{label}</li>;
                    })}
                  </ul>
                </div>
              )}

              {/* Narrative — MENTOR-ONLY per Decision #17 */}
              {/* IMPORTANT: Do not render this block on any student-facing surface. */}
              {assessment.narrativeForMentor && (
                <div className="msd-narrative">
                  <h3 className="msd-narrative-title">{t('directory.studentDetail.assessment.narrativeTitle')}</h3>
                  <p className="msd-narrative-text">{assessment.narrativeForMentor}</p>
                </div>
              )}

              {/* Suggested intervention */}
              {assessment.suggestedIntervention && (
                <div className="msd-intervention">
                  <h3 className="msd-intervention-title">{t('directory.studentDetail.assessment.interventionTitle')}</h3>
                  <p className="msd-intervention-text">{assessment.suggestedIntervention}</p>
                </div>
              )}
            </>
          )}
        </section>

        {/* Recommendations section */}
        <section className="section-card msd-section msd-section--recs">
          <div className="section-head">
            <h2 className="section-head__title">{t('directory.studentDetail.recommendations.sectionTitle')}</h2>
            <button
              type="button"
              className="ds-btn ds-btn-secondary ds-btn-sm"
              onClick={() => setAddRecOpen(true)}
            >
              <Plus aria-hidden="true" />
              {t('directory.studentDetail.recommendations.addButton')}
            </button>
          </div>

          {/* Active recommendations */}
          {activeRecs.length === 0 ? (
            <InlineEmpty kind="idea" title={t('directory.studentDetail.recommendations.empty')} />
          ) : (
            <div className="msd-rec-list">
              {activeRecs.map((rec) => (
                <RecommendationCard
                  key={rec.id}
                  rec={rec}
                  onSuppress={handleSuppressClick}
                />
              ))}
            </div>
          )}

          {/* Suppressed / history section */}
          {inactiveRecs.length > 0 && (
            <div className="msd-suppressed-section">
              <button
                type="button"
                className="ds-btn ds-btn-ghost ds-btn-sm msd-suppressed-toggle"
                onClick={() => setSuppressedExpanded((v) => !v)}
                aria-expanded={suppressedExpanded}
              >
                {t(
                  suppressedExpanded
                    ? 'directory.studentDetail.suppressed.toggleHide'
                    : 'directory.studentDetail.suppressed.toggleShow',
                  { count: inactiveRecs.length }
                )}
              </button>
              {suppressedExpanded && (
                <div className="msd-suppressed-list">
                  {inactiveRecs.map((rec) => (
                    <div key={rec.id} className="msd-suppressed-row">
                      <span className="msd-suppressed-name">
                        {targetTitle(rec, t)}
                      </span>
                      <span className="msd-suppressed-state ds-badge ds-badge-neutral">
                        {STATE_LABEL_KEYS[rec.state] ? t(STATE_LABEL_KEYS[rec.state]) : rec.state}
                      </span>
                      <span className="msd-suppressed-date">
                        {rec.createdAt ? formatRelative(rec.createdAt, { lang }) : '-'}
                      </span>
                      {rec.state === 'MENTOR_SUPPRESSED' && rec.mentorSuppressedReason && (
                        <span className="msd-suppressed-reason">
                          "{rec.mentorSuppressedReason}"
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        {/* Intervention log — placeholder until a dedicated endpoint exists */}
        <section className="section-card msd-section msd-section--log">
          <div className="section-head">
            <h2 className="section-head__title">{t('directory.studentDetail.interventionLog.title')}</h2>
          </div>
          {/* TODO (20-x-b or future story): wire GET /mentor/students/:studentId/interventions
              when the endpoint is implemented. For now, show empty state. */}
          <InlineEmpty kind="docs" title={t('directory.studentDetail.interventionLog.empty')} />
        </section>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  const backLabel = isTeacher ? t('teacher.studentDetail.backToDashboard') : t('directory.studentDetail.backButton');

  return (
    <div className="msd-page">
      <PageHead
        back={{ onClick: () => navigate(isTeacher ? '/teacher/dashboard' : '/mentor/students'), label: backLabel }}
        eyebrow={isTeacher ? t('teacher.studentDetail.parentName') : t('nav.students')}
        title={studentName}
        description={t('directory.studentDetail.pageSubtitle')}
      />

      {/* Tab bar: a mentor switches between the student's progress and the assessment. A teacher came
          from the student's progress page, so for them the assessment stands alone: no tab that
          leads to a placeholder. */}
      {!isTeacher && (
        <div className="msd-tab-bar">
          <SubTabs
            tabs={[
              { key: 'progress', label: t('directory.studentDetail.tabs.progress') },
              { key: 'assessment', label: t('directory.studentDetail.tabs.assessment') },
            ]}
            active={activeTab}
            onChange={setActiveTab}
            ariaLabel={t('directory.studentDetail.tabsAria')}
          />
        </div>
      )}

      {/* Tab panels */}
      {!isTeacher && (
        <div
          role="tabpanel"
          id="tabpanel-progress"
          aria-label={t('directory.studentDetail.tabs.progress')}
          hidden={activeTab !== 'progress'}
        >
          <PageEmptyState
            variant="docs"
            size="sm"
            title={t('directory.studentDetail.progressPlaceholder')}
            actionLabel={t('directory.studentDetail.openProfile')}
            onAction={() => navigate(`/students/${studentId}`)}
          />
        </div>
      )}

      <div
        role={isTeacher ? undefined : 'tabpanel'}
        id="tabpanel-assessment"
        aria-label={t('directory.studentDetail.tabs.assessment')}
        hidden={!isTeacher && activeTab !== 'assessment'}
      >
        {renderAssessmentTab()}
      </div>

      {/* Suppress modal */}
      {suppressTarget && (
        <SuppressModal
          recommendation={suppressTarget}
          onConfirm={handleSuppressConfirm}
          onClose={handleSuppressClose}
          submitting={suppressSubmitting}
        />
      )}

      {/* Add recommendation modal */}
      {addRecOpen && (
        <AddRecommendationModal
          studentId={studentId}
          studentName={studentName}
          onSuccess={handleAddRecSuccess}
          onClose={() => setAddRecOpen(false)}
        />
      )}
    </div>
  );
}

export default MentorStudentDetail;
